import { Application, Container, type FederatedPointerEvent, Graphics, Rectangle } from 'pixi.js';
import { type TweenHandle, TweenRegistry, easeOutQuad } from '../../animation/tween';
import { type Hex, type HexLayout, type PixelPoint, axialToPixel, hexKey } from '../../core/hex';
import type { Tile } from '../../core/tile';
import type {
  HexClickListener,
  HexHoverListener,
  HexVisibilityTiers,
  Renderer,
  RendererInitOptions,
  SetCharacterPositionOptions,
  Unsubscribe,
} from '../Renderer';
import { type GlyphName, isGlyphName } from '../glyphs/glyphPaths';
import {
  isDiscoveredTrap,
  isPathHazard,
  isSpentMerchant,
  tileGlyphForExplored,
} from '../tileGlyphs';
import { type Viewport, cameraTransform, screenToAxialAt } from './camera';
import { CameraTween, type CameraTweenMode } from './cameraTween';
import {
  HEX_OUTLINE_COLOUR,
  HEX_OUTLINE_HOVER_COLOUR,
  type RenderFace,
  dangerBlendedColour,
  faceColour,
  isAlwaysVisibleTerrain,
  showsRealFace,
} from './faceColour';
import { FrontierGlyphPool } from './frontierGlyphPool';
import { buildGlyphGraphics } from './glyphGraphics';
import { PLAYER_MARKER_STYLE } from './playerMarker';
import { frontierPulseAlpha } from './pulse';

interface HexNode {
  readonly hex: Hex;
  readonly graphics: Graphics;
  tile: Tile;
  /** Active discovery-flip tween, or `null` when the node is painting normally. */
  flipTween: TweenHandle | null;
  /**
   * Tile-type glyph overlay (HEX-66; HEX-102 = a Pixi `Graphics` drawn from the
   * bespoke glyph set, was a unicode `Text`). Lazily created the first time the
   * node paints a real face with a non-null glyph. `null` for glyphless types
   * (`ground` / `road`) and for nodes that have only ever shown the `?`
   * cardback. Lives in the renderer's `glyphLayer`, not under `graphics`.
   */
  glyph: Graphics | null;
  /** Glyph name currently drawn on `glyph`, so a name change rebuilds it. */
  glyphName: GlyphName | null;
}

/**
 * Discovery flip moment (ADR-0014 §3 moment 1). Total duration matches the
 * hand-over (220ms easeOutQuad); the |cos(π · eased)| curve gives a fast
 * snap-shut followed by a slower reveal — reads as a card flip while only
 * touching a single Graphics' `scale.x`.
 */
const DISCOVERY_FLIP_DURATION_MS = 220;

/**
 * Movement tween duration (ADR-0014 §3 moment 2). 180ms easeOutQuad — the
 * marker leans into the move then settles. Camera follow stays snap; the
 * marker carrying the eye is enough lateral motion.
 */
const MOVEMENT_TWEEN_DURATION_MS = 180;

function pointyTopCorners(size: number): number[] {
  const points: number[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30);
    points.push(size * Math.cos(angle), size * Math.sin(angle));
  }
  return points;
}

function paintHex(graphics: Graphics, polygon: number[], colour: number, hovered = false): void {
  graphics.clear();
  graphics
    .poly(polygon)
    .fill(colour)
    .stroke({
      color: hovered ? HEX_OUTLINE_HOVER_COLOUR : HEX_OUTLINE_COLOUR,
      width: 1,
      alignment: 1,
    });
}

/**
 * Alpha multiplier applied to a spent traveller-merchant's glyph (HEX-102). The
 * handoff set has no distinct "spent" shape, so a consumed merchant keeps the
 * `merchant` glyph dimmed — functional parity with the pre-HEX-102 hollow flag
 * without inventing iconography (see `isSpentMerchant`).
 */
const SPENT_GLYPH_ALPHA = 0.5;

/**
 * Glyph alpha per render face — the overlay dims in step with the dimmed
 * memory / footprint faces so a remembered tile's glyph reads as "remembered"
 * rather than floating at full brightness over a faded tile.
 */
function glyphAlphaForFace(face: RenderFace): number {
  if (face === 'memory') return 0.5;
  if (face === 'footprint') return 0.75;
  // A distant landmark mountain's glyph sits below memory so the silhouette
  // reads as the furthest thing on the map (HEX-127).
  if (face === 'landmark') return 0.45;
  return 1;
}

/**
 * Should the renderer surface a hover event for `key`? ADR-0014 §5: hover
 * fires for hexes in the current vision disk; memory / frontier / no-tier
 * hexes suppress. Per-tile discovery filtering (active vs frontier hover)
 * is layered on top by the App's hover handler.
 */
export function shouldEmitHover(key: string, tiers: HexVisibilityTiers | null): boolean {
  if (tiers === null) return true;
  return tiers.vision.has(key);
}

/**
 * Pick the rendered face for `key` given the visibility sets and the tile's
 * discovery state. Pure so it can be unit-tested without booting Pixi.
 *
 * Returns `null` for hexes that should not be visible (never seen AND out of
 * vision); the renderer hides the graphics in that case. A seen-but-undiscovered
 * tile out of vision returns `memory-frontier` (a remembered `?`, HEX-128); a
 * distant landmark mountain surfaces as a silhouette (HEX-127).
 */
export function pickRenderFace(
  key: string,
  tile: Tile,
  tiers: HexVisibilityTiers | null,
): RenderFace | null {
  if (tiers === null) return 'natural';
  if (tiers.vision.has(key)) {
    // Content tiles whose identity is still hidden render the `?` cardback.
    if (!showsRealFace(tile)) return 'frontier';
    // Always-visible terrain (mountains / landmarks) is geography, not "owned"
    // by any character — the personal-vs-inherited footprint distinction
    // doesn't apply. Always render natural.
    if (isAlwaysVisibleTerrain(tile.type)) return 'natural';
    // Content tiles you've personally crossed render bright natural ("YOUR
    // map"). Content tiles globally discovered but NOT by you render as
    // footprint (visibly inherited from previous travellers) so your own
    // explored tiles stand out by contrast.
    return tiers.personallyExplored.has(key) ? 'natural' : 'footprint';
  }
  if (tiers.memory.has(key)) {
    // A remembered tile out of vision: real face if its identity is known
    // (discovered / always-visible terrain), otherwise a desaturated `?`
    // remembered-frontier (HEX-128) — geography remembered, content still hidden
    // (HEX-85). Never black: the memory set holds every seen-but-out-of-vision
    // tile, the face split happens here.
    return showsRealFace(tile) ? 'memory' : 'memory-frontier';
  }
  // Distant always-visible terrain (mountains) surfaces as an orientation
  // silhouette beyond the vision/memory radius (HEX-127). Guarded on
  // always-visible terrain so a content tile that strays into the set can never
  // leak its hidden identity — the frontier-mystery invariant (HEX-85) holds.
  if (tiers.landmark?.has(key) === true && isAlwaysVisibleTerrain(tile.type)) return 'landmark';
  return null;
}

/**
 * Player-vision vignette — a soft radial brighten centred on the player hex
 * that falls off to 0 at the edge of the vision disk (ADR-0014 §1). Rendered
 * as a stack of concentric filled circles with decreasing alpha — cheap, no
 * gradient texture, no per-frame tween (it's static given the player hex).
 */
const VIGNETTE_RING_COUNT = 5;
const VIGNETTE_PEAK_ALPHA = 0.1; // ~10% brighten at the player hex per ADR.
const VIGNETTE_COLOUR = 0xf0f0ff;

/**
 * Route-preview styling (HEX-74). A warm amber outline + faint fill on each
 * previewed hex reads as "you'll walk here" without competing with the gold
 * player marker or the cyan pillars.
 */
const PATH_PREVIEW_COLOUR = 0xe0b24a;
const PATH_PREVIEW_FILL_ALPHA = 0.14;
const PATH_PREVIEW_STROKE_ALPHA = 0.85;
/**
 * Hazard route colour (HEX-96). A leg landing on a live encounter or a
 * discovered trap paints red instead of the amber safe colour — "this route is
 * not safe". Brighter fill alpha than the amber so the warning legs read first.
 */
const PATH_HAZARD_COLOUR = 0xd6453a;
const PATH_HAZARD_FILL_ALPHA = 0.24;

/**
 * Paint the route preview: `polygon` translated onto every hex centre. Each
 * hex is amber by default; a hex flagged in `hazards` (live encounter / known
 * trap, HEX-96) paints red so the unsafe legs stand out from the safe ones.
 */
function paintPathPreview(
  graphics: Graphics,
  polygon: number[],
  centres: PixelPoint[],
  hazards: readonly boolean[],
): void {
  graphics.clear();
  for (let c = 0; c < centres.length; c++) {
    const centre = centres[c] as PixelPoint;
    const hazard = hazards[c] === true;
    const colour = hazard ? PATH_HAZARD_COLOUR : PATH_PREVIEW_COLOUR;
    const fillAlpha = hazard ? PATH_HAZARD_FILL_ALPHA : PATH_PREVIEW_FILL_ALPHA;
    const shifted: number[] = [];
    for (let i = 0; i < polygon.length; i += 2) {
      shifted.push((polygon[i] as number) + centre.x, (polygon[i + 1] as number) + centre.y);
    }
    graphics.poly(shifted).fill({ color: colour, alpha: fillAlpha }).stroke({
      color: colour,
      width: 2,
      alpha: PATH_PREVIEW_STROKE_ALPHA,
      alignment: 0.5,
    });
  }
}

function paintVignette(graphics: Graphics, radiusPx: number): void {
  graphics.clear();
  for (let i = VIGNETTE_RING_COUNT - 1; i >= 0; i--) {
    const t = (i + 1) / VIGNETTE_RING_COUNT;
    const r = radiusPx * t;
    const alpha = VIGNETTE_PEAK_ALPHA * (1 - t) * (1 - t);
    if (alpha <= 0.001) continue;
    graphics.circle(0, 0, r).fill({ color: VIGNETTE_COLOUR, alpha });
  }
}

/**
 * Pixi.js v8 renderer. Owns the Pixi `Application`, a world container holding
 * one `Graphics` per hex plus a frontier glyph pool, a player-vision vignette,
 * and the player marker. Per ADR-0007 Pixi imports stay inside this directory;
 * the rest of the codebase goes through the `Renderer` interface.
 */
export class PixiRenderer implements Renderer {
  private app: Application | null = null;
  private worldContainer: Container | null = null;
  private layout: HexLayout | null = null;
  private viewport: Viewport | null = null;
  private cameraTarget: Hex = { q: 0, r: 0 };
  private cameraTween: CameraTween = new CameraTween({ x: 0, y: 0 });
  private hexPolygon: number[] = [];
  private readonly nodes = new Map<string, HexNode>();
  private readonly clickListeners = new Set<HexClickListener>();
  private readonly hoverListeners = new Set<HexHoverListener>();
  private stagePointerHandler: ((e: FederatedPointerEvent) => void) | null = null;
  private stageMoveHandler: ((e: FederatedPointerEvent) => void) | null = null;
  private stageOutHandler: ((e: FederatedPointerEvent) => void) | null = null;
  private tiers: HexVisibilityTiers | null = null;
  private hoveredKey: string | null = null;
  private playerMarker: Graphics | null = null;
  private playerMarkerHex: Hex | null = null;
  private vignette: Graphics | null = null;
  private glyphPool: FrontierGlyphPool | null = null;
  /** Layer holding the per-tile type glyphs (HEX-66); above hexes, below the
   *  frontier `?` pool, vignette and player marker. */
  private glyphLayer: Container | null = null;
  /** Accumulated frame time; drives the shared frontier pulse phase. */
  private elapsedMs = 0;
  /** Tween registry — drives discovery flips, movement, any future Pixi tweens. */
  private readonly tweens = new TweenRegistry();
  /** Active movement tween, or `null` when the marker is parked. */
  private movementTween: TweenHandle | null = null;
  /** Live pixel centre of the player marker — set by snap or by the tween. */
  private playerMarkerPixel: PixelPoint | null = null;
  /**
   * Live zoom factor (HEX-74). Applied as `worldContainer.scale`; the camera +
   * hit-test math fold it into an effective layout (base size × zoom) so the
   * scaled world still maps cleanly between screen and axial space.
   */
  private zoom = 1;
  /** Route-preview overlay (HEX-74); one Graphics painting every previewed hex. */
  private pathPreview: Graphics | null = null;
  /**
   * Free-look pan offset (HEX-96). Added on top of the follow-camera transform
   * so the player can survey the map past the follow camera's furthest
   * scroll-out without moving the character. `{0,0}` while following; `panBy`
   * accumulates it, `recenterCamera` clears it. The follow tween keeps updating
   * underneath, so exiting free-look snaps cleanly back to the character.
   */
  private freeLookOffset: PixelPoint = { x: 0, y: 0 };

  async init(options: RendererInitOptions): Promise<void> {
    if (this.app) return;

    this.layout = options.layout;
    this.viewport = { width: options.viewport.width, height: options.viewport.height };
    this.hexPolygon = pointyTopCorners(options.layout.size);

    const app = new Application();
    await app.init({
      width: options.viewport.width,
      height: options.viewport.height,
      antialias: true,
      background: 0x0c0c10,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
      autoStart: false,
    });
    options.container.appendChild(app.canvas);

    const worldContainer = new Container();
    app.stage.addChild(worldContainer);

    // Type-glyph layer sits above the hex graphics (added before the frontier
    // pool so the `?` cardback floats on top of it; the two never share a tile).
    const glyphLayer = new Container();
    glyphLayer.eventMode = 'none';
    worldContainer.addChild(glyphLayer);
    this.glyphLayer = glyphLayer;

    // Frontier glyph pool floats above tile graphics but below the vignette
    // and player marker so the cardback `?` stays legible while the player's
    // own hex still pops.
    const glyphPool = new FrontierGlyphPool(worldContainer, options.layout.size);
    this.glyphPool = glyphPool;

    app.stage.eventMode = 'static';
    app.stage.hitArea = new Rectangle(0, 0, options.viewport.width, options.viewport.height);
    const onPointerDown = (e: FederatedPointerEvent): void => this.handlePointerDown(e);
    const onPointerMove = (e: FederatedPointerEvent): void => this.handlePointerMove(e);
    const onPointerOut = (e: FederatedPointerEvent): void => this.handlePointerOut(e);
    app.stage.on('pointerdown', onPointerDown);
    app.stage.on('pointermove', onPointerMove);
    app.stage.on('pointerout', onPointerOut);
    this.stagePointerHandler = onPointerDown;
    this.stageMoveHandler = onPointerMove;
    this.stageOutHandler = onPointerOut;

    this.app = app;
    this.worldContainer = worldContainer;
    worldContainer.scale.set(this.zoom);
    // Seed the tween at the current target's transform so the first frame
    // sits where it should without any visual snap from {0, 0}.
    const initial = cameraTransform(this.cameraTarget, this.viewport, this.effectiveLayout());
    this.cameraTween = new CameraTween({ x: initial.x, y: initial.y });
    this.applyCameraTransform();
    app.render();
  }

  addHex(hex: Hex, tile: Tile): void {
    const container = this.worldContainer;
    const layout = this.layout;
    if (!container || !layout) return;

    const key = hexKey(hex);
    const existing = this.nodes.get(key);
    if (existing) {
      container.removeChild(existing.graphics);
      existing.graphics.destroy();
      existing.glyph?.destroy();
      this.nodes.delete(key);
    }

    const graphics = new Graphics();
    const pixel = axialToPixel(hex, layout);
    graphics.position.set(pixel.x, pixel.y);
    const face = pickRenderFace(key, tile, this.tiers);
    if (face === null) {
      graphics.visible = false;
    } else {
      const hovered = key === this.hoveredKey && face !== 'frontier';
      paintHex(graphics, this.hexPolygon, this.faceFill(key, face, tile), hovered);
    }
    container.addChild(graphics);
    const node: HexNode = { hex, graphics, tile, flipTween: null, glyph: null, glyphName: null };
    this.nodes.set(key, node);
    this.syncNodeGlyph(node, face);
    // Keep the glyph layer, frontier glyphs, route preview, vignette, and the
    // player marker (HEX-41) on top of newly-added hex graphics.
    this.restackOverlays();
    this.refreshFrontierGlyphs();
  }

  updateHex(hex: Hex, tile: Tile): void {
    const key = hexKey(hex);
    const node = this.nodes.get(key);
    if (!node) return;
    // Detect the discovery moment: !showsRealFace → showsRealFace. This is the
    // exact transition the §C flip animation visualises.
    const wasFrontier = !showsRealFace(node.tile);
    const isReal = showsRealFace(tile);
    node.tile = tile;
    if (wasFrontier && isReal && node.flipTween === null && this.tiers !== null) {
      // Only flip when the tile is in the current vision disk — discoveries
      // outside view repaint silently when the player walks back. Without
      // this gate, every off-screen tile would flip on `addHex` after the
      // first paint of the session, which is jarring.
      if (this.tiers.vision.has(key)) {
        this.startDiscoveryFlip(key, node);
        this.refreshFrontierGlyphs();
        return;
      }
    }
    this.paintNode(key, node);
    this.refreshFrontierGlyphs();
  }

  /**
   * Run the discovery flip on `node`: collapse scale.x to 0 showing the
   * frontier face, then expand back to 1 showing the real face. Single tween
   * uses `|cos(π · eased)|` for the geometry and switches the painted face at
   * `eased >= 0.5`. The renderer cancels any prior flip on the same node
   * before starting (so back-to-back discoveries don't pile up).
   */
  private startDiscoveryFlip(key: string, node: HexNode): void {
    node.flipTween?.cancel();
    // The flip's first half shows the `?` cardback; hide any glyph until the
    // real face settles (re-shown by `paintNode` in `onComplete`).
    if (node.glyph) node.glyph.visible = false;
    node.flipTween = this.tweens.start({
      durationMs: DISCOVERY_FLIP_DURATION_MS,
      easing: easeOutQuad,
      onUpdate: (eased) => {
        node.graphics.scale.x = Math.abs(Math.cos(Math.PI * eased));
        // First half paints the frontier slate (cardback); second half paints
        // the natural face — the swap happens at scale.x = 0 so it is invisible.
        const face: RenderFace =
          eased < 0.5 ? 'frontier' : (pickRenderFace(key, node.tile, this.tiers) ?? 'natural');
        paintHex(node.graphics, this.hexPolygon, this.faceFill(key, face, node.tile));
      },
      onComplete: () => {
        node.graphics.scale.x = 1;
        node.flipTween = null;
        this.paintNode(key, node);
      },
    });
  }

  setHexVisibility(tiers: HexVisibilityTiers): void {
    this.tiers = tiers;
    // Drop stale hover state if the previously-hovered hex is no longer in
    // vision — handlePointerMove's gate would refuse to set it now, so the
    // outline brighten would be a phantom.
    if (this.hoveredKey !== null && !tiers.vision.has(this.hoveredKey)) {
      this.applyHoverState(null);
    }
    for (const [key, node] of this.nodes) {
      this.paintNode(key, node);
    }
    this.refreshFrontierGlyphs();
  }

  private paintNode(key: string, node: HexNode): void {
    // Active flip owns the graphic — repainting here would race the tween.
    if (node.flipTween !== null) return;
    const face = pickRenderFace(key, node.tile, this.tiers);
    if (face === null) {
      node.graphics.visible = false;
      this.syncNodeGlyph(node, null);
      return;
    }
    node.graphics.visible = true;
    // Hover outline only brightens for non-frontier hexes — frontier cards
    // get the `?` glyph brighten via the glyph pool instead (ADR-0014 §5).
    const hovered = key === this.hoveredKey && face !== 'frontier';
    paintHex(node.graphics, this.hexPolygon, this.faceFill(key, face, node.tile), hovered);
    this.syncNodeGlyph(node, face);
  }

  /**
   * Fill colour for a hex (HEX-96): the face colour, blended toward the
   * sprung-trap tint when the tile is a danger tile this character has crossed
   * (in `personallyExplored`). Never tints a `?` card-back (live `frontier` or
   * remembered `memory-frontier`) — an undiscovered danger tile must stay
   * indistinguishable from ground (HEX-85).
   */
  private faceFill(key: string, face: RenderFace, tile: Tile): number {
    const base = faceColour(face, tile);
    if (face === 'frontier' || face === 'memory-frontier') return base;
    return isDiscoveredTrap(tile, this.tiers?.personallyExplored, key)
      ? dangerBlendedColour(base)
      : base;
  }

  /**
   * Ensure `node`'s tile-type glyph matches its current face (HEX-66). Hidden
   * for the `?` cardback (live `frontier` or remembered `memory-frontier`) and
   * for not-rendered tiles (`null`) so undiscovered content never leaks its
   * identity; lazily created on first real-face paint, dimmed in step with
   * memory / footprint faces.
   */
  private syncNodeGlyph(node: HexNode, face: RenderFace | null): void {
    const layer = this.glyphLayer;
    const layout = this.layout;
    if (!layer || !layout) return;
    // A `?` cardback (live or remembered) and not-rendered tiles never show a
    // type glyph — undiscovered identity must not leak.
    if (face === null || face === 'frontier' || face === 'memory-frontier') {
      if (node.glyph) node.glyph.visible = false;
      return;
    }
    const glyphName = tileGlyphForExplored(
      node.tile,
      this.tiers?.personallyExplored,
      hexKey(node.hex),
    );
    // `null` is the deliberate "no overlay" sentinel; an unmapped/`undefined`
    // name (a legacy `TileType` whose glyph never existed) degrades the same way
    // — a glyph-less tile, not a thrown render (HEX-109).
    if (!isGlyphName(glyphName)) {
      if (node.glyph) node.glyph.visible = false;
      return;
    }
    // Render boundary (HEX-109): one tile's glyph must never abort the whole
    // tile-paint loop into a black world. The glyph build is total now (empty
    // graphics for any unmapped name), but a try/catch surfaces any future
    // single-tile failure to the console while the rest of the world still paints.
    try {
      let glyph = node.glyph;
      // Rebuild when first shown or when the glyph identity changed (e.g. a live
      // encounter resolving to the death mark) — Graphics geometry is baked, so a
      // name change means a new node rather than an in-place text swap.
      if (!glyph || node.glyphName !== glyphName) {
        glyph?.destroy();
        glyph = buildGlyphGraphics(glyphName, layout.size);
        const pixel = axialToPixel(node.hex, layout);
        glyph.position.set(pixel.x, pixel.y);
        layer.addChild(glyph);
        node.glyph = glyph;
        node.glyphName = glyphName;
      }
      // A spent traveller-merchant keeps the `merchant` glyph but dimmed (HEX-102).
      const spentDim = isSpentMerchant(node.tile) ? SPENT_GLYPH_ALPHA : 1;
      glyph.alpha = glyphAlphaForFace(face) * spentDim;
      glyph.visible = true;
    } catch (err) {
      console.error(`HEX-109: glyph paint failed at ${hexKey(node.hex)} (${glyphName})`, err);
      if (node.glyph) node.glyph.visible = false;
    }
  }

  /**
   * Walk the node set, assign a glyph slot to every frontier hex (in vision
   * AND undiscovered), and release glyphs whose hexes have left the frontier.
   * Stable under repeated calls — `FrontierGlyphPool.syncTo` keeps existing
   * assignments in place.
   */
  private refreshFrontierGlyphs(): void {
    const pool = this.glyphPool;
    const layout = this.layout;
    const tiers = this.tiers;
    if (!pool || !layout) return;
    const positions = new Map<string, { x: number; y: number }>();
    if (tiers !== null) {
      for (const [key, node] of this.nodes) {
        // A frontier glyph only belongs on tiles whose identity is hidden —
        // always-visible terrain (mountains / landmarks) shows its real face
        // and doesn't get a `?` glyph.
        if (tiers.vision.has(key) && !showsRealFace(node.tile)) {
          const p = axialToPixel(node.hex, layout);
          positions.set(key, { x: p.x, y: p.y });
        }
      }
    }
    pool.syncTo(positions);
    pool.setAlpha(frontierPulseAlpha(this.elapsedMs));
  }

  setCameraTarget(hex: Hex, mode: CameraTweenMode = 'snap'): void {
    this.cameraTarget = hex;
    const layout = this.layout;
    const viewport = this.viewport;
    if (!layout || !viewport) return;
    const target = cameraTransform(hex, viewport, this.effectiveLayout());
    this.cameraTween.setTarget({ x: target.x, y: target.y }, mode);
    this.applyCameraTransform();
  }

  setZoom(zoom: number): void {
    const next = zoom > 0 ? zoom : this.zoom;
    if (next === this.zoom) return;
    this.zoom = next;
    this.worldContainer?.scale.set(next);
    // Re-centre on the current camera target at the new scale. Zoom is a
    // deliberate user action, so a snap recentre (rather than a pan) keeps the
    // character pinned to the viewport centre without a lurch.
    this.setCameraTarget(this.cameraTarget, 'snap');
  }

  getZoom(): number {
    return this.zoom;
  }

  panBy(dx: number, dy: number): void {
    this.freeLookOffset = { x: this.freeLookOffset.x + dx, y: this.freeLookOffset.y + dy };
    this.applyCameraTransform();
  }

  recenterCamera(): void {
    if (this.freeLookOffset.x === 0 && this.freeLookOffset.y === 0) return;
    this.freeLookOffset = { x: 0, y: 0 };
    // Snap back to the live camera target — the discontinuity is intentional,
    // exactly like a teleport (ADR-0014 §4). `setCameraTarget` re-applies the
    // transform with the now-cleared offset.
    this.setCameraTarget(this.cameraTarget, 'snap');
  }

  /** Test seam — the live free-look pan offset ({0,0} while following). */
  getFreeLookOffset(): PixelPoint {
    return this.freeLookOffset;
  }

  setPathPreview(hexes: readonly Hex[] | null): void {
    const container = this.worldContainer;
    const layout = this.layout;
    if (!container || !layout) return;
    if (!hexes || hexes.length === 0) {
      this.pathPreview?.clear();
      return;
    }
    let preview = this.pathPreview;
    if (!preview) {
      preview = new Graphics();
      preview.eventMode = 'none';
      container.addChild(preview);
      this.pathPreview = preview;
    }
    const centres = hexes.map((hex) => axialToPixel(hex, layout));
    // Flag legs that land on a live encounter or a discovered trap (HEX-96) so
    // they paint red — "this route is not safe".
    const hazards = hexes.map((hex) => this.isPathHazardKey(hexKey(hex)));
    paintPathPreview(preview, this.hexPolygon, centres, hazards);
    // Keep the preview below the marker / vignette but above tile faces.
    this.restackOverlays();
  }

  /**
   * Is the previewed hex `key` a route hazard (HEX-96)? Reads the rendered
   * tile snapshot + the personally-explored set the renderer already holds, so
   * the App passes no extra payload. Unknown / unrendered hexes are not hazards.
   */
  private isPathHazardKey(key: string): boolean {
    const node = this.nodes.get(key);
    if (!node) return false;
    return isPathHazard(node.tile, this.tiers?.personallyExplored, key);
  }

  /**
   * Base layout scaled by the live zoom factor (HEX-74). Hex nodes are
   * positioned with the unscaled base layout and the world container carries
   * the zoom as a `scale`; camera + hit-test math must therefore reason in the
   * scaled space, which this effective layout expresses (both `axialToPixel`
   * and `pixelToAxial` are linear in `size`).
   */
  private effectiveLayout(): HexLayout {
    const layout = this.layout as HexLayout;
    return { size: layout.size * this.zoom, origin: layout.origin };
  }

  /**
   * Re-stack the non-hex layers to the top of the world container in z-order:
   * type glyphs, frontier `?` pool, route preview, vignette, player marker.
   * Called after any `addChild` that would otherwise bury them under a freshly
   * added hex graphic.
   */
  private restackOverlays(): void {
    const container = this.worldContainer;
    if (!container) return;
    if (this.glyphLayer) container.addChild(this.glyphLayer);
    this.glyphPool?.bringToFront();
    if (this.pathPreview) container.addChild(this.pathPreview);
    if (this.vignette) container.addChild(this.vignette);
    if (this.playerMarker) container.addChild(this.playerMarker);
  }

  setCharacterPosition(hex: Hex | null, options: SetCharacterPositionOptions = {}): void {
    if (hex === null) {
      this.movementTween?.cancel();
      this.movementTween = null;
      if (this.playerMarker) {
        this.playerMarker.destroy();
        this.playerMarker = null;
      }
      if (this.vignette) {
        this.vignette.destroy();
        this.vignette = null;
      }
      this.playerMarkerHex = null;
      this.playerMarkerPixel = null;
      return;
    }
    const layout = this.layout;
    if (!layout) return;
    const targetPixel = axialToPixel(hex, layout);
    const mode = options.mode ?? 'snap';
    const previousPixel = this.playerMarkerPixel;
    this.playerMarkerHex = { q: hex.q, r: hex.r };

    // Hidden → visible always snaps; smooth without a previous pixel has
    // nothing to interpolate from.
    if (mode === 'snap' || previousPixel === null) {
      this.movementTween?.cancel();
      this.movementTween = null;
      this.playerMarkerPixel = targetPixel;
      this.repaintVignette();
      this.repaintPlayerMarker();
      return;
    }

    // Smooth: tween from the marker's current pixel to the target pixel. The
    // vignette and the marker share the same pixel cursor so they pan together.
    this.movementTween?.cancel();
    const fromX = previousPixel.x;
    const fromY = previousPixel.y;
    const dx = targetPixel.x - fromX;
    const dy = targetPixel.y - fromY;
    this.movementTween = this.tweens.start({
      durationMs: MOVEMENT_TWEEN_DURATION_MS,
      easing: easeOutQuad,
      onUpdate: (eased) => {
        this.playerMarkerPixel = { x: fromX + dx * eased, y: fromY + dy * eased };
        this.repaintVignette();
        this.repaintPlayerMarker();
      },
      onComplete: () => {
        this.playerMarkerPixel = targetPixel;
        this.movementTween = null;
        this.repaintVignette();
        this.repaintPlayerMarker();
      },
    });
  }

  /** Test seam: current marker hex, or `null` if hidden. */
  getCharacterMarkerPosition(): Hex | null {
    return this.playerMarkerHex;
  }

  /** Test seam: glyph pool — useful for asserting visible-frontier count. */
  getGlyphPool(): FrontierGlyphPool | null {
    return this.glyphPool;
  }

  private repaintVignette(): void {
    const container = this.worldContainer;
    const layout = this.layout;
    const pixel = this.playerMarkerPixel;
    if (!container || !layout || pixel === null) return;
    let vignette = this.vignette;
    if (!vignette) {
      vignette = new Graphics();
      vignette.eventMode = 'none';
      container.addChild(vignette);
      this.vignette = vignette;
    } else {
      container.addChild(vignette);
    }
    // Vignette spans the active vision disk (2 hex steps in either direction).
    // Pointy-top hex column width is `size * sqrt(3)`; 2.4× gives a touch of
    // headroom so the falloff never has a hard edge inside the disk.
    const visionPx = layout.size * Math.sqrt(3) * 2.4;
    vignette.position.set(pixel.x, pixel.y);
    paintVignette(vignette, visionPx);
  }

  private repaintPlayerMarker(): void {
    const container = this.worldContainer;
    const layout = this.layout;
    const pixel = this.playerMarkerPixel;
    if (!container || !layout || pixel === null) return;
    let marker = this.playerMarker;
    if (!marker) {
      marker = new Graphics();
      // Marker sits above every other layer by being added last to the world
      // container. Subsequent `addHex` calls would re-stack it — both
      // `repaintPlayerMarker` and `addHex` re-add to keep z-order honest.
      container.addChild(marker);
      this.playerMarker = marker;
    } else {
      container.addChild(marker);
    }
    // Inner radii / pip stay aligned with the hex (HEX-41 PlayerMarkerStyle
    // is a per-hex-size scale); the actual centre is the live tweened pixel.
    const outerRadius = layout.size * PLAYER_MARKER_STYLE.outerRadius;
    const innerRadius = layout.size * PLAYER_MARKER_STYLE.innerRadius;
    const pipRadius = layout.size * PLAYER_MARKER_STYLE.pipRadius;
    marker.position.set(pixel.x, pixel.y);
    marker.clear();
    marker
      .circle(0, 0, outerRadius)
      .stroke({ color: PLAYER_MARKER_STYLE.outerColour, width: PLAYER_MARKER_STYLE.outerWidth })
      .circle(0, 0, innerRadius)
      .stroke({ color: PLAYER_MARKER_STYLE.innerColour, width: PLAYER_MARKER_STYLE.innerWidth })
      .circle(0, 0, pipRadius)
      .fill(PLAYER_MARKER_STYLE.pipColour);
  }

  onHexClick(listener: HexClickListener): Unsubscribe {
    this.clickListeners.add(listener);
    return () => {
      this.clickListeners.delete(listener);
    };
  }

  onHexHover(listener: HexHoverListener): Unsubscribe {
    this.hoverListeners.add(listener);
    return () => {
      this.hoverListeners.delete(listener);
    };
  }

  frame(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    this.tweens.advance(deltaMs);
    if (this.cameraTween.tick(deltaMs)) {
      this.applyCameraTransform();
    }
    if (this.glyphPool && this.glyphPool.visibleCount() > 0) {
      this.glyphPool.setAlpha(frontierPulseAlpha(this.elapsedMs));
    }
    this.app?.render();
  }

  destroy(): void {
    const app = this.app;
    if (!app) return;
    if (this.stagePointerHandler) {
      app.stage.off('pointerdown', this.stagePointerHandler);
      this.stagePointerHandler = null;
    }
    if (this.stageMoveHandler) {
      app.stage.off('pointermove', this.stageMoveHandler);
      this.stageMoveHandler = null;
    }
    if (this.stageOutHandler) {
      app.stage.off('pointerout', this.stageOutHandler);
      this.stageOutHandler = null;
    }
    this.clickListeners.clear();
    this.hoverListeners.clear();
    this.tweens.cancelAll();
    this.movementTween = null;
    for (const node of this.nodes.values()) {
      node.flipTween?.cancel();
      node.graphics.destroy();
      node.glyph?.destroy();
    }
    this.nodes.clear();
    this.glyphPool?.destroy();
    this.glyphPool = null;
    if (this.glyphLayer) {
      this.glyphLayer.destroy({ children: true });
      this.glyphLayer = null;
    }
    if (this.pathPreview) {
      this.pathPreview.destroy();
      this.pathPreview = null;
    }
    if (this.vignette) {
      this.vignette.destroy();
      this.vignette = null;
    }
    if (this.playerMarker) {
      this.playerMarker.destroy();
      this.playerMarker = null;
    }
    this.playerMarkerHex = null;
    this.playerMarkerPixel = null;
    this.tiers = null;
    this.hoveredKey = null;
    this.elapsedMs = 0;
    this.zoom = 1;
    this.freeLookOffset = { x: 0, y: 0 };
    app.destroy(true, { children: true });
    this.app = null;
    this.worldContainer = null;
    this.layout = null;
    this.viewport = null;
  }

  private applyCameraTransform(): void {
    const container = this.worldContainer;
    if (!container) return;
    const t = this.currentCameraOffset();
    container.position.set(t.x, t.y);
  }

  /**
   * Live world-container offset (HEX-96): the follow-camera tween plus the
   * free-look pan offset. The single source of truth for both painting the
   * container and hit-testing the pointer, so screen→axial stays correct while
   * the player is surveying in free-look.
   */
  private currentCameraOffset(): PixelPoint {
    const t = this.cameraTween.current();
    return { x: t.x + this.freeLookOffset.x, y: t.y + this.freeLookOffset.y };
  }

  private handlePointerDown(event: FederatedPointerEvent): void {
    const layout = this.layout;
    if (!layout || this.clickListeners.size === 0) return;
    const coord = screenToAxialAt(
      { x: event.global.x, y: event.global.y },
      this.currentCameraOffset(),
      this.effectiveLayout(),
    );
    const payload = { q: coord.q, r: coord.r, shiftKey: event.shiftKey };
    for (const listener of this.clickListeners) listener(payload);
  }

  private handlePointerMove(event: FederatedPointerEvent): void {
    const layout = this.layout;
    if (!layout) return;
    const screenX = event.global.x;
    const screenY = event.global.y;
    const coord = screenToAxialAt(
      { x: screenX, y: screenY },
      this.currentCameraOffset(),
      this.effectiveLayout(),
    );
    const key = hexKey(coord);
    // Hover AFFORDANCE (outline brighten + frontier `?` glyph pin) stays vision-
    // scoped per ADR-0014 §5.
    if (shouldEmitHover(key, this.tiers)) {
      if (key !== this.hoveredKey) this.applyHoverState(key);
    } else if (this.hoveredKey !== null) {
      this.applyHoverState(null);
    }
    // Hover EVENTS emit for every hex — HEX-74 route preview must react to
    // out-of-vision (memory) targets too. The App's hover handler re-gates the
    // info panel to the vision disk, so broadening here doesn't leak panels.
    if (this.hoverListeners.size === 0) return;
    const payload = { q: coord.q, r: coord.r, screenX, screenY };
    for (const listener of this.hoverListeners) listener(payload);
  }

  private handlePointerOut(_event: FederatedPointerEvent): void {
    if (this.hoveredKey === null) return;
    this.applyHoverState(null);
    for (const listener of this.hoverListeners) listener(null);
  }

  /**
   * Apply the hover affordance for `key`: repaint the previously hovered tile
   * back to its baseline outline, repaint the new one with the brighter
   * outline, and pin the frontier glyph alpha if the new hex is a frontier
   * cardback. ADR-0014 §5.
   */
  private applyHoverState(newKey: string | null): void {
    const previous = this.hoveredKey;
    if (previous === newKey) return;
    this.hoveredKey = newKey;
    if (previous !== null) {
      const prev = this.nodes.get(previous);
      if (prev) this.paintNode(previous, prev);
    }
    if (newKey !== null) {
      const next = this.nodes.get(newKey);
      if (next) this.paintNode(newKey, next);
    }
    if (this.glyphPool) {
      if (newKey === null) {
        this.glyphPool.setHoveredKey(null);
      } else {
        const node = this.nodes.get(newKey);
        const isFrontier =
          node !== undefined && pickRenderFace(newKey, node.tile, this.tiers) === 'frontier';
        this.glyphPool.setHoveredKey(isFrontier ? newKey : null);
      }
    }
  }

  /** Test seam — the hex currently under the pointer, or `null`. */
  getHoveredKey(): string | null {
    return this.hoveredKey;
  }

  /** Test seam — the current visual camera offset (interpolated mid-tween). */
  getCameraVisualTransform(): { x: number; y: number } {
    return this.cameraTween.current();
  }
}
